import type * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn.ts';
import { SectionLabel } from '../section-label/section-label.tsx';

export interface SidebarProps extends Omit<React.ComponentProps<'nav'>, 'style'> {
  /*
   * What the sidebar is a list of — the section name, on the same 36px line as
   * the rail logo and the page title. Given as a slot for the same reason the
   * rail takes its logo that way: the caller decides whether it is a link.
   */
  header?: React.ReactNode;
}

/*
 * The second navigation column: the pages inside the section the rail has you
 * standing in. A pane, not a card — full height, its own fill, a hairline
 * against the page, and it scrolls inside itself, because a section can hold
 * seventeen tools and the page beside it must not grow a scrollbar for them.
 *
 * The shell gives it a grid track of its own (`--sidebar-w`), so the page
 * centres in what is left exactly as it does beside the rail alone.
 *
 * Spacing is the rail's, to the pixel: pt-9 puts the header on the title line
 * and the 54px below it drop the first page onto the 130px line where page
 * bodies start. The three columns read across.
 */
export function Sidebar({ header, className, children, ...props }: SidebarProps) {
  return (
    <nav
      data-slot='sidebar'
      className={cn(
        /* Same banner arithmetic as the rail: `--app-banner-h` is 0 where there is none. */
        'sticky top-[var(--app-banner-h,0px)] h-[calc(100dvh-var(--app-banner-h,0px))]',
        'flex flex-col overflow-y-auto px-3 pt-9 pb-6',
        'max-mobile:hidden',
        /*
         * The rail's fill, so rail and sidebar read as one navigation zone
         * against the canvas. A real border rather than the rail's `::after`
         * overlay: this box scrolls, and an overlay pinned inside it scrolls too.
         */
        'border-r border-line bg-fill-faint',
        className,
      )}
      {...props}
    >
      {header}
      <div className={cn('flex flex-col gap-5', header && 'mt-13.5')}>{children}</div>
    </nav>
  );
}

export type SidebarHeaderProps = Omit<React.ComponentProps<'div'>, 'style'>;

/*
 * The section's name at the top of the sidebar. Not a heading: the nav around
 * it already carries these same words as its accessible name, and a heading
 * would have a screen reader say them twice.
 *
 * The height is the rail logo's, so the pages below stay on their line.
 */
export function SidebarHeader({ className, ...props }: SidebarHeaderProps) {
  return (
    <div
      data-slot='sidebar-header'
      className={cn(
        'flex h-10 shrink-0 items-center px-2 text-base font-semibold text-ink',
        className,
      )}
      {...props}
    />
  );
}

export interface SidebarSectionProps extends Omit<
  React.ComponentProps<'div'>,
  'style' | 'title'
> {
  /* Heading above this run of pages. Absent where the sidebar reads as one list. */
  title?: React.ReactNode;
}

/* One run of pages, with the small label that names it where there is one. */
export function SidebarSection({
  title,
  className,
  children,
  ...props
}: SidebarSectionProps) {
  return (
    <div
      data-slot='sidebar-section'
      className={cn('flex flex-col gap-0.5', className)}
      {...props}
    >
      {title !== undefined && title !== null && (
        <SectionLabel as='h2' className='px-2 pb-1'>
          {title}
        </SectionLabel>
      )}
      {children}
    </div>
  );
}

/*
 * One page in the sidebar. Variants rather than a component because the element
 * is the consumer's to choose — a router Link here, a plain anchor in a story —
 * exactly as railLinkVariants is used.
 *
 * The current page is a white tile on the pane's fill, the same way the rail
 * marks the current tile: weight and colour move with it, so it is never the
 * tint alone doing the work.
 */
export const sidebarItemVariants = cva(
  [
    'flex w-full items-center truncate rounded-md px-2 py-1.5 text-left text-sm',
    'transition-colors',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
  ],
  {
    variants: {
      active: {
        true: 'bg-surface font-semibold text-ink',
        false: 'font-medium text-ink-secondary hover:bg-fill hover:text-ink',
      },
    },
    defaultVariants: {
      active: false,
    },
  },
);

export type SidebarItemVariants = VariantProps<typeof sidebarItemVariants>;
